import { ReactElement, useCallback, useMemo, useState } from 'react'
import { mapValueKey } from '@editorUtils'
import { filter, find } from 'lodash-es'
import { Box, useTheme } from '@mui/material'
import { Form } from '@ui/forms'
import { Tabs as DetailTabs } from '@ui/navigation'
import { useModelStore, useResultsStore, useTransmissionStore } from '@editorStores'
import { useElementType, useAllDomains, useResultsSelection } from '@editorHooks'
import { memberToDomain } from 'src/components/pages/Editor/utils/positionToDomain'
import useDesignFormNavigationGuard from '../../hooks/useDesignFormNavigationGuard'
import { useEnhanceLoadsWithDomain } from '../../hooks/useEnhanceLoadsWithDomain'
import { getWallGuidForStandardRipMember } from '../../utils'
import Results2DetailCloseButton from '../Results2DetailCloseButton'
import { MemberChecksSection } from '../Results2DetailPane'
import Results2LoadResults from '../Results2DetailPane/Results2LoadResults'
import type { Results2RelationLink } from '../Results2DetailPane/components/Results2RelationLinksRow'
import type { DesignFormRootValues } from '../Results2DetailPane/types'
import type { Results2ListItem } from '../Results2ElementList'
import Results2PaneHeader from '../Results2PaneHeader'

type DetailPaneTabId = 'design' | 'loads'

export type Results2StandardRipDetailPaneProps = {
  elementId: string
  selectedItem: Results2ListItem
  disableForm?: boolean
}

const standardRipAssemblyLine = (item: Results2ListItem): string | undefined => {
  const name = item.assemblyName?.trim()
  return name || undefined
}

const Results2StandardRipDetailPane = ({
  elementId,
  selectedItem,
  disableForm = false,
}: Results2StandardRipDetailPaneProps): ReactElement => {
  const theme = useTheme()
  const guardDesignFormNav = useDesignFormNavigationGuard()
  const [tab, setTab] = useState<DetailPaneTabId>('design')
  const assemblyLine = standardRipAssemblyLine(selectedItem)
  const subtitle = `${selectedItem.type} · ${selectedItem.qs}`
  const { selectResultsPosition: selectPosition } = useResultsSelection()

  const structuralChecks = useResultsStore(state => state.structuralChecks)
  const standardRipChecks = useResultsStore(state => state.standardRipChecks)
  const elementLoads = useResultsStore(state => state.elementLoads)
  const positionLoads = useResultsStore(state => state.positionLoads)

  const domains = useAllDomains()
  const verticalTransmissionGraph = useTransmissionStore(state => state.verticalTransmissionGraph)
  const elementType = useElementType(elementId)

  const rips = useModelStore(state => state.model.rips)
  const lintels = useModelStore(state => state.model.lintels)
  const verticalSlabs = useModelStore(state => state.model.vertical_slabs)
  const verticalRoofSlabs = useModelStore(state => state.model.vertical_roof_slabs)

  const memberCheckSourceChecks = useMemo((): CombinedPositionCheck[] => {
    const ripEntry = find(standardRipChecks, c => c.standard_rip_member.guid === elementId)
    const fromRip = ripEntry?.standard_rip_checks
    if (fromRip?.length) {
      return fromRip as CombinedPositionCheck[]
    }
    return structuralChecks ? filter(structuralChecks, { element_guid: elementId }) : []
  }, [standardRipChecks, structuralChecks, elementId])

  const formValuesForChecks = useMemo(
    (): DesignFormRootValues => ({
      elements: [
        {
          checksSelectedElement: memberCheckSourceChecks,
        } as DesignFormRootValues['elements'][number],
      ],
      supportConfigIndexToElementIndex: {},
    }),
    [memberCheckSourceChecks],
  )

  const loads = useMemo(
    () => [...(elementLoads || []), ...(positionLoads || [])],
    [elementLoads, positionLoads],
  )

  const allPositions = useMemo(
    () => (rips && lintels ? [...rips, ...lintels] : []),
    [rips, lintels],
  )

  const slabBeams = useMemo(
    () =>
      verticalSlabs && verticalRoofSlabs
        ? [...verticalSlabs, ...verticalRoofSlabs].map(slab => slab.beam)
        : [],
    [verticalSlabs, verticalRoofSlabs],
  )

  const allLoadsWithDomain = useEnhanceLoadsWithDomain({
    loads,
    domains,
    verticalTransmissionGraph,
    slabBeams,
  })

  const guidToPosition = useMemo(() => mapValueKey(allPositions, 'position_guid'), [allPositions])
  const slabBeamGuidToBeam = useMemo(() => mapValueKey(slabBeams), [slabBeams])

  const selectedStandardRipLoads = useMemo(() => {
    const selectedCheck = find(standardRipChecks, check => {
      return check.standard_rip_member.guid === elementId
    })
    if (!selectedCheck) return []
    const standardRipDomain = memberToDomain(selectedCheck.standard_rip_member)
    return selectedCheck.standard_rip_loads.map(load => ({
      ...load,
      domain: standardRipDomain,
    })) as PointLoadWithDomain[]
  }, [standardRipChecks, elementId])

  const filteredLoads = useMemo(() => {
    if (selectedStandardRipLoads.length > 0) {
      return selectedStandardRipLoads as (AreaLoad | DomainLoad)[]
    }
    const position = guidToPosition[elementId] || slabBeamGuidToBeam[elementId]
    const currentElementGuid =
      position && position.position_guid ? position.position_guid : elementId
    return filter(allLoadsWithDomain || [], load => load.element_guid === currentElementGuid) as (
      | AreaLoad
      | DomainLoad
    )[]
  }, [selectedStandardRipLoads, guidToPosition, elementId, slabBeamGuidToBeam, allLoadsWithDomain])

  const linkedWallGuid = useMemo(
    () => getWallGuidForStandardRipMember(standardRipChecks, elementId),
    [elementId, standardRipChecks],
  )

  const handleSelectLinkedWall = useCallback(() => {
    if (!linkedWallGuid) return
    if (guardDesignFormNav()) return
    selectPosition(linkedWallGuid, 'walls', 'wall-assemblies')
  }, [guardDesignFormNav, linkedWallGuid, selectPosition])

  const relationLinks = useMemo((): Results2RelationLink[] => {
    if (!linkedWallGuid) return []
    return [
      {
        label: 'zur Wand',
        tooltip: 'Wand auswählen',
        onClick: handleSelectLinkedWall,
      },
    ]
  }, [handleSelectLinkedWall, linkedWallGuid])

  const defaultLoadTab = useMemo<'point-load' | 'line-load'>(() => {
    if (
      elementType === 'lintels' ||
      elementType === 'beams' ||
      elementType === 'purlins' ||
      elementType === 'roof_slab_beams' ||
      elementType === 'slab_beams'
    ) {
      return 'line-load'
    }
    return 'point-load'
  }, [elementType])

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        background: theme.palette.common.white,
      }}
    >
      {tab !== 'design' ? (
        <Results2PaneHeader
          title={selectedItem.label ?? elementId}
          subtitle={subtitle + ' · ' + assemblyLine}
          // assemblyLine={assemblyLine}
          relationLinks={relationLinks}
        />
      ) : null}
      <Box sx={{ flexShrink: 0, position: 'relative' }}>
        <DetailTabs
          items={[
            { id: 'design', label: 'Bemessung' },
            { id: 'loads', label: 'Lasten' },
          ]}
          value={tab}
          onChange={id => setTab(id as DetailPaneTabId)}
          size="md"
          kind="folder"
        />
        <Results2DetailCloseButton />
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {tab === 'design' ? (
          <>
            <Results2PaneHeader
              title={selectedItem.label ?? elementId}
              subtitle={subtitle + ' · ' + assemblyLine}
              // assemblyLine={assemblyLine}
              relationLinks={relationLinks}
            />
            <Box sx={{ px: 2, py: 1.5 }}>
              <Form
                enableReinitialize
                defaultValues={formValuesForChecks}
                onSubmit={async () => undefined}
                noValidate
                style={{ display: 'contents' }}
              >
                <MemberChecksSection
                  activeSettingsTab={null}
                  elementGuid={elementId}
                  disableForm={disableForm}
                />
              </Form>
            </Box>
          </>
        ) : (
          <Box sx={{ px: 2, pb: 2 }}>
            <Results2LoadResults
              loads={filteredLoads}
              defaultLoadTab={defaultLoadTab}
              elementId={elementId}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default Results2StandardRipDetailPane
